import { useLocation, useNavigate } from 'react-router-dom'
import { useSession } from '../lib/session'

const Ico = ({ d, fill }) => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'}
       stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
)

const SONGWRITER_TABS = [
  {
    // The browsable feed — what other writers have opened out to you. Your
    // own catalog, drafts included, lives on your profile instead; the two
    // used to share a screen, which meant unfinished private work sat next
    // to things written to be discovered.
    to: '/home', label: 'Home',
    icon: <Ico d={<><path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V20h13V9.5" /></>} />,
  },
  {
    // Concentric, not two overlapping discs — the same shape as the reach dial,
    // so the tab and the thing it leads to are visibly the same idea.
    to: '/circles', label: 'Circles',
    icon: <Ico d={<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4.4" /></>} />,
  },
  {
    // A new song, or a new person — both start with one tap on Create.
    to: '/create', label: '', name: 'Create', primary: true,
    icon: <Ico d={<><path d="M12 5v14" /><path d="M5 12h14" /></>} />,
  },
  {
    to: '/collective', label: 'Collective',
    icon: <Ico d={<><circle cx="12" cy="12" r="9" /><path d="M3 12h18" />
      <path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" /></>} />,
  },
]

/**
 * Not a fixed route — where it points depends on where the songwriter is in
 * the church relationship, so this is built per-render in TabBar rather than
 * living as a plain entry in SONGWRITER_TABS.
 */
function churchTabFor(approved) {
  return {
    to: approved.length > 0 ? '/licences' : '/join',
    label: 'Church',
    icon: <Ico d={<path d="M12 3l7 3.4v6.1c0 4.8-3 7.9-7 9-4-1.1-7-4.2-7-9V6.4L12 3Z" />} />,
  }
}

/** A reviewer only ever has one job on this app: the queue in front of them. */
const REVIEWER_TABS = [
  {
    to: '/inbox', label: 'Needs you',
    icon: <Ico d={<><path d="M4 4h16v12H7l-3 3V4Z" /></>} />,
  },
]

/** An admin's two screens — who's in, and how review works here — were only
 *  ever reachable from each other via a back-link. Desktop is what exposed
 *  that there was no real nav between them; give both a permanent way in. */
const ADMIN_TABS = [
  {
    to: '/admin/people', label: 'People',
    icon: <Ico d={<><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 8.5a2.6 2.6 0 1 1 0-5.2" /><path d="M15 12.2a5 5 0 0 1 6 4.8" /></>} />,
  },
  {
    to: '/admin/process', label: 'Process',
    icon: <Ico d={<><path d="M4 6h16" /><path d="M4 12h10" /><path d="M4 18h13" /></>} />,
  },
]

/** The <Platform> routes in App.jsx, and only those: what the moderators
 *  decided, and what people have written in about. */
const PLATFORM_TABS = [
  {
    to: '/admin/platform', label: 'Business',
    icon: <Ico d={<><path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-7" /><path d="M21 20H3" /></>} />,
  },
  {
    to: '/admin/moderation', label: 'Moderation',
    icon: <Ico d={<><path d="M12 3l7 3.4v6.1c0 4.8-3 7.9-7 9-4-1.1-7-4.2-7-9V6.4L12 3Z" />
      <path d="m9 12 2.2 2.2L15.4 10" /></>} />,
  },
  {
    to: '/admin/feedback', label: 'Support',
    icon: <Ico d={<><path d="M4 5h16v11H8l-4 4V5Z" /><path d="M9 10h6" /></>} />,
  },
  {
    to: '/admin/requests', label: 'Requests',
    icon: <Ico d={<><path d="M6 3h9l4 4v14H6V3Z" /><path d="M14 3v5h5" /><path d="M9 14h6" /></>} />,
  },
]

/** `isPlatformOnly` is checked before the seat, not after: a superadmin who
 *  also holds a church seat keeps that seat's tabs — see session.jsx for why
 *  the flag is seat-aware rather than a bare superadmin test. */
export function tabsForRole(role, isPlatformOnly) {
  if (isPlatformOnly) return PLATFORM_TABS
  if (role === 'reviewer') return REVIEWER_TABS
  if (role === 'admin') return ADMIN_TABS
  return SONGWRITER_TABS
}

/** The five-way footer on a phone, and the same list as a rail on desktop —
 *  see Shell.jsx for which shape renders where. */
export default function TabBar() {
  const nav = useNavigate()
  const { pathname } = useLocation()
  const { role, approved, isPlatformOnly } = useSession()
  const base = tabsForRole(role, isPlatformOnly)
  // Church isn't a fixed route — where it points depends on membership status
  // — so it's spliced in here rather than living in SONGWRITER_TABS. It goes
  // immediately before Collective, which puts Create dead centre of five:
  // Home · Circles · + · Church · Collective.
  const collectiveIdx = base.findIndex((t) => t.to === '/collective')
  const TABS = collectiveIdx === -1 ? base : [
    ...base.slice(0, collectiveIdx), churchTabFor(approved), ...base.slice(collectiveIdx),
  ]
  const activeIndex = TABS.findIndex((t) => pathname === t.to)
  const showIndicator = activeIndex !== -1 && !TABS[activeIndex].primary

  return (
    <nav className="tabbar" aria-label="Main"
         style={{ gridTemplateColumns: `repeat(${TABS.length}, 1fr)` }}>
      <span
        className="tab-indicator"
        style={{ transform: `translateX(${activeIndex * 100}%)`, width: `calc((100% - 12px)/${TABS.length})`, opacity: showIndicator ? 1 : 0 }}
        aria-hidden="true"
      />
      {TABS.map((t) => {
        const active = pathname === t.to
        return (
          <button
            key={t.to}
            className={`tab${active ? ' on' : ''}${t.primary ? ' primary' : ''}`}
            onClick={() => nav(t.to)}
            aria-current={active ? 'page' : undefined}
            aria-label={t.label ? undefined : t.name}
          >
            <span className="tab-ic">{t.icon}</span>
            <span className="tab-lb">{t.label}</span>
          </button>
        )
      })}
    </nav>
  )
}
